import React, { useState, useEffect } from 'react';
import { emailsService, inscriptionService } from '../services/api';
import '../styles/GestionEmails.css';
import logger from '../utils/logger';

const EnvoiEmailTemplateModal = ({ onSend, onClose, initialTemplate = null }) => {
  const [step, setStep] = useState(initialTemplate ? 2 : 1); // Si template initial, on passe direct à l'étape 2
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(initialTemplate);
  const [inscriptions, setInscriptions] = useState([]);
  const [selectedInscription, setSelectedInscription] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);

  // Fonction helper pour obtenir l'email du destinataire
  const getDestinataireEmail = (inscription) => {
    if (!inscription) return null;

    // Essayer d'abord le responsable de l'inscription
    if (inscription.responsable_email) {
      return inscription.responsable_email;
    }

    // Si pas de responsable, utiliser le parent de l'enfant
    if (inscription.enfant_details?.parent?.email) {
      return inscription.enfant_details.parent.email;
    }

    return null;
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await emailsService.getTemplates();
      setTemplates(response.data.results || response.data);
    } catch (err) {
      setError('Erreur lors du chargement des templates');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const searchInscriptions = async (query) => {
    if (!query || query.length < 2) {
      setInscriptions([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Recherche par ID, email parent ou nom enfant
      const response = await inscriptionService.getAll({
        search: query
      });
      const results = response.data.results || response.data;
      logger.log('[EnvoiEmailTemplateModal] Inscriptions trouvées:', results);
      setInscriptions(Array.isArray(results) ? results : []);
    } catch (err) {
      logger.error('[EnvoiEmailTemplateModal] Erreur lors de la recherche:', err);
      setError(err.response?.data?.detail || err.response?.data?.message || 'Erreur lors de la recherche');
      setInscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    // Debounce la recherche
    const timeoutId = setTimeout(() => {
      searchInscriptions(value);
    }, 300);
    return () => clearTimeout(timeoutId);
  };

  const handlePreview = async () => {
    if (!selectedTemplate || !selectedInscription) return;

    setLoading(true);
    try {
      const response = await emailsService.previewTemplate(
        selectedTemplate.id,
        selectedInscription.id
      );
      setPreview(response.data);
      setStep(3);
    } catch (err) {
      setError('Erreur lors de la prévisualisation');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!selectedTemplate || !selectedInscription) return;

    // Vérifier qu'un email destinataire existe
    const destinataireEmail = getDestinataireEmail(selectedInscription);
    if (!destinataireEmail) {
      setError('Impossible d\'envoyer l\'email : aucune adresse email trouvée pour cette inscription. Veuillez vérifier que le responsable ou le parent a bien un email renseigné.');
      return;
    }

    setLoading(true);
    try {
      await emailsService.envoyerTemplate({
        template_id: selectedTemplate.id,
        inscription_id: selectedInscription.id
      });
      onSend();
      onClose();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Erreur lors de l\'envoi de l\'email';
      setError(errorMsg);
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content large">
        <div className="modal-header">
          <h2>Envoyer un email avec template</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        {error && (
          <div className="alert alert-danger" style={{ margin: '15px' }}>
            {error}
            <button onClick={() => setError(null)}>&times;</button>
          </div>
        )}

        <div className="modal-body">
          {/* Indicateur d'étapes */}
          <div className="steps-indicator" style={{ display: 'flex', justifyContent: 'space-around', marginBottom: '20px', padding: '0 20px' }}>
            <div className={`step ${step >= 1 ? 'active' : ''}`} style={{ flex: 1, textAlign: 'center' }}>
              <div style={{
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                backgroundColor: step >= 1 ? '#3b82f6' : '#e5e7eb',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 5px'
              }}>1</div>
              <small>Template</small>
            </div>
            <div className={`step ${step >= 2 ? 'active' : ''}`} style={{ flex: 1, textAlign: 'center' }}>
              <div style={{
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                backgroundColor: step >= 2 ? '#3b82f6' : '#e5e7eb',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 5px'
              }}>2</div>
              <small>Inscription</small>
            </div>
            <div className={`step ${step >= 3 ? 'active' : ''}`} style={{ flex: 1, textAlign: 'center' }}>
              <div style={{
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                backgroundColor: step >= 3 ? '#3b82f6' : '#e5e7eb',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 5px'
              }}>3</div>
              <small>Aperçu</small>
            </div>
          </div>

          {/* Étape 1: Choix du template */}
          {step === 1 && (
            <div className="step-content">
              <h3>Sélectionnez un template</h3>
              {loading ? (
                <div className="loading">Chargement...</div>
              ) : (
                <div className="templates-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {templates.filter(t => t.actif).map((template) => (
                    <div
                      key={template.id}
                      className={`template-option ${selectedTemplate?.id === template.id ? 'selected' : ''}`}
                      onClick={() => setSelectedTemplate(template)}
                      style={{
                        padding: '15px',
                        margin: '10px 0',
                        border: selectedTemplate?.id === template.id ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        backgroundColor: selectedTemplate?.id === template.id ? '#eff6ff' : 'white'
                      }}
                    >
                      <h4 style={{ margin: '0 0 5px 0' }}>{template.nom}</h4>
                      <p style={{ margin: '0 0 5px 0', fontSize: '14px', color: '#6b7280' }}>
                        <strong>Sujet:</strong> {template.sujet}
                      </p>
                      {template.evenement_declencheur_display && (
                        <span className="badge badge-info" style={{ fontSize: '12px' }}>
                          {template.evenement_declencheur_display}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Étape 2: Choix de l'inscription */}
          {step === 2 && (
            <div className="step-content">
              <h3>Rechercher une inscription</h3>
              <div className="form-group">
                <input
                  type="text"
                  placeholder="Rechercher par ID, email parent, nom enfant..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  style={{ width: '100%', padding: '10px', fontSize: '14px' }}
                  autoFocus
                />
                <small>Tapez au moins 2 caractères pour rechercher</small>
              </div>

              {loading ? (
                <div className="loading">Recherche...</div>
              ) : inscriptions.length > 0 ? (
                <div className="inscriptions-list" style={{ maxHeight: '350px', overflowY: 'auto', marginTop: '15px' }}>
                  {inscriptions.map((inscription) => (
                    <div
                      key={inscription.id}
                      className={`inscription-option ${selectedInscription?.id === inscription.id ? 'selected' : ''}`}
                      onClick={() => setSelectedInscription(inscription)}
                      style={{
                        padding: '15px',
                        margin: '10px 0',
                        border: selectedInscription?.id === inscription.id ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        backgroundColor: selectedInscription?.id === inscription.id ? '#eff6ff' : 'white'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <div>
                          <h4 style={{ margin: '0 0 5px 0' }}>
                            INS-{String(inscription.id).padStart(5, '0')}
                          </h4>
                          <p style={{ margin: '0 0 3px 0', fontSize: '14px' }}>
                            <strong>Enfant:</strong> {inscription.enfant_details?.prenom || 'N/A'} {inscription.enfant_details?.nom || ''}
                          </p>
                          <p style={{ margin: '0 0 3px 0', fontSize: '14px', color: '#6b7280' }}>
                            <strong>Parent:</strong> {
                              (() => {
                                // Essayer d'abord le responsable de l'inscription
                                let nom = `${inscription.responsable_prenom || ''} ${inscription.responsable_nom || ''}`.trim();
                                let email = inscription.responsable_email;

                                // Si pas de responsable, utiliser le parent de l'enfant
                                if (!nom && !email && inscription.enfant_details?.parent) {
                                  const parent = inscription.enfant_details.parent;
                                  nom = `${parent.prenom || ''} ${parent.nom || ''}`.trim();
                                  email = parent.email;
                                }

                                // Formater l'affichage
                                if (nom && email) {
                                  return `${nom} (${email})`;
                                } else if (email) {
                                  return email;
                                } else if (nom) {
                                  return nom;
                                } else {
                                  return 'Non renseigné';
                                }
                              })()
                            }
                          </p>
                          <p style={{ margin: '0', fontSize: '14px', color: '#6b7280' }}>
                            <strong>Séjour:</strong> {inscription.sejour_details?.titre || inscription.sejour_details?.nom || 'Non renseigné'}
                          </p>
                        </div>
                        <span className={`badge badge-${inscription.statut === 'CONFIRMEE' ? 'success' : inscription.statut === 'EN_ATTENTE' ? 'warning' : 'secondary'}`}>
                          {inscription.statut}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : searchQuery.length >= 2 ? (
                <p style={{ textAlign: 'center', color: '#6b7280', marginTop: '20px' }}>
                  Aucune inscription trouvée
                </p>
              ) : null}
            </div>
          )}

          {/* Étape 3: Prévisualisation et confirmation */}
          {step === 3 && preview && (
            <div className="step-content">
              <h3>Prévisualisation de l'email</h3>
              <div className="preview-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <div className="preview-section">
                  <h4>Destinataire</h4>
                  <p><strong>
                    {(() => {
                      const email = getDestinataireEmail(selectedInscription);

                      if (!email) {
                        return <span style={{ color: '#ef4444' }}>⚠ Aucun email trouvé pour cette inscription</span>;
                      }

                      // Construire le nom complet
                      let nom = '';
                      if (selectedInscription.responsable_email === email) {
                        nom = `${selectedInscription.responsable_prenom || ''} ${selectedInscription.responsable_nom || ''}`.trim();
                      } else if (selectedInscription.enfant_details?.parent) {
                        const parent = selectedInscription.enfant_details.parent;
                        nom = `${parent.prenom || ''} ${parent.nom || ''}`.trim();
                      }

                      return nom ? `${nom} <${email}>` : email;
                    })()}
                  </strong></p>
                </div>
                <div className="preview-section">
                  <h4>Sujet</h4>
                  <p>{preview.sujet}</p>
                </div>
                <div className="preview-section">
                  <h4>Corps du message</h4>
                  <div
                    style={{
                      border: '1px solid #e5e7eb',
                      padding: '15px',
                      borderRadius: '6px',
                      backgroundColor: '#f9fafb'
                    }}
                    dangerouslySetInnerHTML={{ __html: preview.corps }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Annuler
          </button>
          {step > 1 && (
            <button
              className="btn-secondary"
              onClick={() => setStep(step - 1)}
            >
              Précédent
            </button>
          )}
          {step === 1 && (
            <button
              className="btn-primary"
              onClick={() => setStep(2)}
              disabled={!selectedTemplate}
            >
              Suivant
            </button>
          )}
          {step === 2 && (
            <button
              className="btn-primary"
              onClick={handlePreview}
              disabled={!selectedInscription}
            >
              Prévisualiser
            </button>
          )}
          {step === 3 && (
            <button
              className="btn-primary"
              onClick={handleSend}
              disabled={loading || !getDestinataireEmail(selectedInscription)}
              title={!getDestinataireEmail(selectedInscription) ? 'Aucun email destinataire trouvé' : ''}
            >
              {loading ? 'Envoi...' : 'Envoyer l\'email'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnvoiEmailTemplateModal;
